import { useLocation, useNavigate } from "react-router-dom";
import { Landmark, LogOut, MessageSquare, MonitorSmartphone, Users, Settings } from "lucide-react";

import { cn, initialsOf } from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/features/auth/store";
import logoUrl from "@/oblinor-logo.svg";

const NAV = [
  { icon: MessageSquare, label: "Chat", to: "/dashboard" },
  { icon: Landmark, label: "Lånestatus", to: "/laanestatus", loanerOnly: true },
  { icon: Users, label: "Grupperom", to: "/grupperom" },
  { icon: MonitorSmartphone, label: "Enheter", to: "/enheter" },
  { icon: Settings, label: "Innstillinger", to: "/innstillinger" },
];

/** Far-left vertical nav rail. The avatar at the bottom holds the logout menu. */
export function IconRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <nav className="flex w-16 shrink-0 flex-col items-center gap-1 border-r border-white/5 py-4">
      <img src={logoUrl} alt="Oblinor" className="mb-3 size-9 rounded-xl" />

      {NAV.filter((n) => !n.loanerOnly || user?.role === "loaner").map(({ icon: Icon, label, to }) => {
        const active = location.pathname === to;
        return (
          <button
            key={label}
            type="button"
            title={label}
            aria-label={label}
            aria-current={active}
            onClick={() => navigate(to)}
            className={cn(
              "flex size-10 items-center justify-center rounded-xl transition-colors",
              active
                ? "bg-teal-400/15 text-teal-300"
                : "text-white/45 hover:bg-white/5 hover:text-white"
            )}
          >
            <Icon className="size-5" />
          </button>
        );
      })}

      <div className="mt-auto">
        <DropdownMenu>
          <DropdownMenuTrigger
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
            aria-label="Konto"
          >
            <Avatar
              initials={initialsOf(user?.name ?? user?.email)}
              className="bg-teal-500/30 text-teal-100"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end">
            <DropdownMenuLabel>
              <div className="truncate">{user?.name ?? "Innlogget"}</div>
              {user?.email && (
                <div className="truncate text-xs font-normal text-muted-foreground">
                  {user.email}
                </div>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onLogout}>
              <LogOut className="size-4" />
              Logg ut
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
